import { useCallback, useEffect, useId, useRef, useState } from "react";
import { IconPlus, IconSettings, IconX } from "@tabler/icons-react";
import { coreGet, coreMutate } from "../../core-api/api-client";
import "./case-tags.css";

type Tag = { id: string; name: string; version: number; selected: boolean };
type TagView = { tags: Tag[] };
type Command =
  | { action: "create"; name: string }
  | { action: "rename"; tagId: string; name: string; expectedVersion: number }
  | { action: "delete"; tagId: string; expectedVersion: number }
  | { action: "attach" | "detach"; tagId: string };
const errorText = (error: unknown) =>
  error instanceof Error ? error.message : "标签操作失败，请重试";

export function CaseTags({
  caseId,
  canApply,
  canManage,
  onChange,
}: {
  caseId: string;
  canApply: boolean;
  canManage: boolean;
  onChange?: () => void;
}) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [managing, setManaging] = useState(false);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState<Tag | null>(null);
  const [editName, setEditName] = useState("");
  const dialog = useRef<HTMLDialogElement>(null);
  const picker = useRef<HTMLDivElement>(null);
  const locked = useRef(false);
  const sequence = useRef(0);
  const titleId = useId();
  const pickerId = useId();
  const path = `/case-workspace/${encodeURIComponent(caseId)}/tags`;

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      const request = ++sequence.current;
      setLoading(true);
      setError("");
      try {
        const view = await coreGet<TagView>(path, signal);
        if (request === sequence.current && !signal?.aborted)
          setTags(view.tags);
      } catch (cause) {
        if (request === sequence.current && !signal?.aborted)
          setError(errorText(cause));
      } finally {
        if (request === sequence.current && !signal?.aborted) setLoading(false);
      }
    },
    [path],
  );

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => {
      controller.abort();
      sequence.current++;
    };
  }, [refresh]);

  useEffect(() => {
    if (managing) dialog.current?.showModal();
    else dialog.current?.close();
  }, [managing]);

  useEffect(() => {
    if (!expanded) return;
    const closeOutside = (event: PointerEvent) => {
      if (!picker.current?.contains(event.target as Node)) setExpanded(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [expanded]);

  async function change(command: Command) {
    if (locked.current || loading) return false;
    locked.current = true;
    setBusy(true);
    setError("");
    const request = ++sequence.current;
    try {
      const view = await coreMutate<TagView>(path, "POST", command);
      if (request === sequence.current) {
        setTags(view.tags);
        onChange?.();
      }
      return true;
    } catch (cause) {
      if (request === sequence.current) setError(errorText(cause));
      return false;
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }

  const disabled = busy || loading;
  const feedback = error ? (
    <div className="case-tag-error" role="alert">
      {error}{" "}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setEditing(null);
          void refresh();
        }}
      >
        刷新
      </button>
    </div>
  ) : null;

  return (
    <section className="case-tags" aria-label="做标记">
      <h2>做标记</h2>
      <div
        className="case-tag-tools"
        ref={picker}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setExpanded(false);
            picker.current?.querySelector<HTMLButtonElement>("button")?.focus();
          }
        }}
      >
        {canApply && (
          <button
            type="button"
            className="case-tag-icon"
            aria-label="选择标签"
            title="选择标签"
            aria-expanded={expanded}
            aria-controls={pickerId}
            disabled={busy}
            onClick={() => {
              setExpanded(!expanded);
              if (!expanded) void refresh();
            }}
          >
            <IconPlus size={15} aria-hidden="true" />
          </button>
        )}
        {canManage && (
          <button
            type="button"
            className="case-tag-icon"
            aria-label="管理标签"
            title="管理标签"
            disabled={busy}
            onClick={() => {
              setExpanded(false);
              setManaging(true);
              void refresh();
            }}
          >
            <IconSettings size={15} aria-hidden="true" />
          </button>
        )}
        {canApply && expanded && (
          <div
            className="case-tag-picker"
            id={pickerId}
            role="group"
            aria-label="我的标签"
          >
            {loading && <small role="status">加载中…</small>}
            {!loading && !tags.length && (
              <small>暂无标签，请先点击管理图标新增</small>
            )}
            {tags.map((tag) => (
              <label key={tag.id}>
                <input
                  type="checkbox"
                  checked={tag.selected}
                  disabled={disabled}
                  onChange={() =>
                    void change({
                      action: tag.selected ? "detach" : "attach",
                      tagId: tag.id,
                    })
                  }
                />
                <span>{tag.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="case-tag-chips" aria-label="已选标签">
        {tags
          .filter((tag) => tag.selected)
          .map((tag) => (
            <span className="case-tag-chip" key={tag.id}>
              {tag.name}
            </span>
          ))}
      </div>
      {!managing && feedback}
      <dialog
        ref={dialog}
        className="case-tag-dialog"
        aria-labelledby={titleId}
        onCancel={() => setManaging(false)}
        onClose={() => {
          setManaging(false);
          setEditing(null);
        }}
      >
        <div className="case-tag-dialog-heading">
          <h2 id={titleId}>管理标签</h2>
          <button
            type="button"
            className="case-tag-icon"
            aria-label="关闭标签管理"
            onClick={() => setManaging(false)}
          >
            <IconX size={16} aria-hidden="true" />
          </button>
        </div>
        <p className="case-tag-hint">仅你可见，可在多个案件中使用。</p>
        {managing && feedback}
        <form
          className="case-tag-create"
          onSubmit={async (event) => {
            event.preventDefault();
            if (await change({ action: "create", name })) setName("");
          }}
        >
          <input
            aria-label="新标签名称"
            placeholder="输入标签名称"
            value={name}
            maxLength={48}
            required
            disabled={disabled}
            onChange={(event) => setName(event.target.value)}
          />
          <button disabled={disabled || !name.trim()}>新增标签</button>
        </form>
        {loading && <p role="status">加载中…</p>}
        {!loading && !tags.length && (
          <p className="case-tag-hint">还没有标签</p>
        )}
        <ul className="case-tag-manager-list">
          {tags.map((tag) => (
            <li key={tag.id}>
              {editing?.id === tag.id ? (
                <form
                  onSubmit={async (event) => {
                    event.preventDefault();
                    if (
                      await change({
                        action: "rename",
                        tagId: tag.id,
                        name: editName,
                        expectedVersion: editing.version,
                      })
                    )
                      setEditing(null);
                  }}
                >
                  <input
                    autoFocus
                    aria-label="修改标签名称"
                    value={editName}
                    maxLength={48}
                    required
                    disabled={disabled}
                    onChange={(event) => setEditName(event.target.value)}
                  />
                  <button disabled={disabled || !editName.trim()}>保存</button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setEditing(null)}
                  >
                    取消
                  </button>
                </form>
              ) : (
                <>
                  <span>{tag.name}</span>
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={`修改 ${tag.name}`}
                    onClick={() => {
                      setEditing(tag);
                      setEditName(tag.name);
                    }}
                  >
                    修改
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={`删除 ${tag.name}`}
                    onClick={() => {
                      if (
                        window.confirm(
                          `删除标签“${tag.name}”？这会移除你在所有案件中使用的该标签。`,
                        )
                      )
                        void change({
                          action: "delete",
                          tagId: tag.id,
                          expectedVersion: tag.version,
                        });
                    }}
                  >
                    删除
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      </dialog>
    </section>
  );
}
